import React, { useMemo } from 'react';

import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import EditNoteIcon from '@mui/icons-material/EditNote';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { formatDateShort } from '~/services/TimeService';

import currencyFormatter from '~/utils/currencyFormatter';
import { daysOfWeekMessage } from '~/utils/daysOfWeek';

import { useDealInformationTabCount } from '../../DealInformationTabCounts';
import TabPanelShell from '../../shared/TabPanelShell';

import BooleanChip from './BooleanChip';
import {
  getApplicableNightCount,
  getReservationSurchargeAvailability,
  groupReservationSurcharges,
} from './surchargeUtils';
import type { SurchargesTabProps } from './types';

const availabilityLabels = {
  live: 'Live now',
  upcoming: 'Upcoming',
  expired: 'Expired',
  'inactive-today': 'Not active today',
  invalid: 'Invalid dates',
} as const;

function getAvailabilityChipColor(availability: keyof typeof availabilityLabels) {
  switch (availability) {
    case 'live':
      return 'success' as const;
    case 'upcoming':
      return 'info' as const;
    case 'expired':
      return 'default' as const;
    default:
      return 'warning' as const;
  }
}

export default function SurchargesTab({
  canAddSurcharges,
  error,
  isLoading,
  isLoadingReservationSurcharges,
  onAddSurcharges,
  reservationSurcharges,
  reservationSurchargesError,
  surcharges,
}: SurchargesTabProps) {
  useDealInformationTabCount('surcharges', isLoading || error ? undefined : surcharges.length);
  const roomSurchargeGroups = useMemo(() => groupReservationSurcharges(reservationSurcharges), [reservationSurcharges]);
  const now = dayjs.tz();

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading surcharges.</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">Unable to load Salesforce surcharges.</Alert>;
  }

  return (
    <TabPanelShell
      title="Surcharges"
      actions={
        <Button
          disabled={!canAddSurcharges}
          onClick={onAddSurcharges}
          startIcon={<AddIcon />}
          sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          variant="contained"
        >
          Apply Salesforce surcharges
        </Button>
      }
      contentSx={{ p: 0 }}
    >
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
        <Table size="small" sx={{ minWidth: 1300 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.14),
                  color: 'info.dark',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                },
              }}
            >
              <TableCell colSpan={13}>Parent Salesforce surcharges</TableCell>
            </TableRow>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
                  color: 'info.dark',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                },
              }}
            >
              <TableCell>#</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Days of week</TableCell>
              <TableCell align="center">Nights</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell align="right">Converted Cost</TableCell>
              <TableCell align="right">Sell Price</TableCell>
              <TableCell align="right">Margin</TableCell>
              <TableCell>Collection</TableCell>
              <TableCell align="center">Accumulate</TableCell>
              <TableCell align="center">Overlapping</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {surcharges.length === 0 && (
              <TableRow>
                <TableCell colSpan={13}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No Salesforce surcharges found for this offer.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {surcharges.map((surcharge, index) => {
              const margin = surcharge.margin;
              const hasMargin = typeof margin === 'number';

              return (
                <TableRow key={surcharge.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{surcharge.name}</TableCell>
                  <TableCell>{formatDateShort(surcharge.start_date)}</TableCell>
                  <TableCell>{formatDateShort(surcharge.end_date)}</TableCell>
                  <TableCell>{daysOfWeekMessage(surcharge)}</TableCell>
                  <TableCell align="center">{getApplicableNightCount(surcharge)}</TableCell>
                  <TableCell align="right">
                    {currencyFormatter(surcharge.currency_code, surcharge.cost_price)}
                  </TableCell>
                  <TableCell align="right">{currencyFormatter('AUD', surcharge.converted_cost)}</TableCell>
                  <TableCell align="right">
                    {currencyFormatter(surcharge.currency_code, surcharge.sell_price)}
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      color={hasMargin ? (margin >= 20 ? 'success.main' : 'warning.main') : 'error.main'}
                      fontWeight={700}
                      variant="body2"
                    >
                      {hasMargin ? `${margin.toFixed(1)}%` : 'Missing'}
                    </Typography>
                  </TableCell>
                  <TableCell>{surcharge.collected_by}</TableCell>
                  <TableCell align="center">
                    <BooleanChip value={surcharge.accumulate} />
                  </TableCell>
                  <TableCell align="center">
                    <BooleanChip value={surcharge.overlapping} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
      <Stack spacing={1.5} sx={{ p: 3 }}>
        <Stack spacing={0.25}>
          <Typography variant="subtitle1">Surcharges applied to room rates</Typography>
          <Typography color="text.secondary" variant="body2">
            These surcharge blocks are currently on the room rates of the property associated with this offer. Applying
            Salesforce surcharges replaces the existing blocks on each room rate you select, including blocks with no
            matching Salesforce surcharge on this opportunity.
          </Typography>
        </Stack>
        <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'auto', width: 'max-content' }}>
            <TableHead>
              <TableRow
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: 'grey.50',
                    color: 'text.secondary',
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 0,
                    textTransform: 'uppercase',
                  },
                }}
              >
                <TableCell>Room</TableCell>
                <TableCell>Rate plan</TableCell>
                <TableCell>Applied surcharge blocks</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingReservationSurcharges && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Stack alignItems="center" direction="row" justifyContent="center" spacing={1} sx={{ py: 3 }}>
                      <CircularProgress size={16} />
                      <Typography color="text.secondary" variant="body2">
                        Loading room-rate surcharges.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {!isLoadingReservationSurcharges && reservationSurchargesError && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Alert severity="error">Unable to load room-rate surcharges.</Alert>
                  </TableCell>
                </TableRow>
              )}
              {!isLoadingReservationSurcharges && !reservationSurchargesError && roomSurchargeGroups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                      No surcharge date blocks are applied to this offer&apos;s room rates.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!isLoadingReservationSurcharges &&
                !reservationSurchargesError &&
                roomSurchargeGroups.map((roomGroup) =>
                  roomGroup.rateGroups.map((rateGroup, rateIndex) => (
                    <TableRow key={rateGroup.roomRateId}>
                      {rateIndex === 0 && (
                        <TableCell
                          rowSpan={roomGroup.rateGroups.length}
                          sx={{ bgcolor: 'grey.50', verticalAlign: 'top' }}
                        >
                          <Typography fontWeight={700} variant="body2">
                            {roomGroup.roomName}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Typography
                          component={rateGroup.href ? 'a' : 'span'}
                          {...(rateGroup.href ? { href: rateGroup.href, target: '_blank', rel: 'noreferrer' } : {})}
                          color={rateGroup.href ? 'primary' : 'text.primary'}
                          variant="body2"
                        >
                          {[rateGroup.ratePlanName, rateGroup.ratePlanCode].filter(Boolean).join(' - ')}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Stack spacing={0.75}>
                          {rateGroup.surcharges.map((assignment) => {
                            const linkedSurcharges = assignment.linkedSalesforceSurchargeIds
                              .map((id) => surcharges.find((surcharge) => surcharge.id === id)?.name ?? id)
                              .join(', ');
                            const availability = getReservationSurchargeAvailability(
                              assignment.reservationSurcharge,
                              now,
                            );

                            return (
                              <Stack
                                key={assignment.reservationSurcharge.id}
                                alignItems={{ sm: 'center' }}
                                direction={{ sm: 'row' }}
                                spacing={1}
                              >
                                <Typography variant="body2">
                                  {formatDateShort(assignment.reservationSurcharge.start_date)} to{' '}
                                  {formatDateShort(assignment.reservationSurcharge.end_date)} ·{' '}
                                  {daysOfWeekMessage(assignment.reservationSurcharge)}
                                </Typography>
                                <Typography color="text.secondary" variant="body2">
                                  {currencyFormatter(
                                    assignment.reservationSurcharge.cost_currency,
                                    assignment.reservationSurcharge.cost_amount,
                                  )}{' '}
                                  cost ·{' '}
                                  {currencyFormatter(
                                    assignment.reservationSurcharge.cost_currency,
                                    assignment.reservationSurcharge.prices?.[
                                      assignment.reservationSurcharge.cost_currency
                                    ],
                                  )}{' '}
                                  sell
                                </Typography>
                                <Chip
                                  color={getAvailabilityChipColor(availability)}
                                  label={availabilityLabels[availability]}
                                  size="small"
                                  variant="outlined"
                                />
                                <Chip
                                  color={linkedSurcharges ? 'success' : 'default'}
                                  label={
                                    linkedSurcharges ? `Matches: ${linkedSurcharges}` : 'No match on this opportunity'
                                  }
                                  size="small"
                                  variant="outlined"
                                />
                              </Stack>
                            );
                          })}
                        </Stack>
                      </TableCell>
                      <TableCell align="center" sx={{ verticalAlign: 'top' }}>
                        {rateGroup.href && (
                          <Tooltip title="Manage room-rate surcharges">
                            <IconButton
                              aria-label="Manage room-rate surcharges"
                              color="primary"
                              component="a"
                              href={rateGroup.href}
                              rel="noreferrer"
                              target="_blank"
                            >
                              <EditNoteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  )),
                )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </TabPanelShell>
  );
}
